import { create } from 'zustand';
import { EditorState } from '../types';
import { webrService } from '../services/webrService';

interface EditorStore extends EditorState {
  setCode: (code: string) => void;
  setOutput: (output: string) => void;
  setRunning: (isRunning: boolean) => void;
  setError: (error: string | null) => void;
  clearOutput: () => void;
  runCode: () => Promise<void>;
  runSelection: (selectedCode: string) => Promise<void>;
  resetDetectionStates: () => void;
  markPlotAsSeen: () => void;
  markDataFrameAsSeen: () => void;
  setCurrentDataFrame: (index: number) => void;
  setCurrentPlot: (index: number) => void;
  addExecutionStep: (step: import('../types').ExecutionStep) => void;
}

export const useEditorStore = create<EditorStore>((set, get) => ({
  code: `# 欢迎使用R语言！
# 试试下面的示例代码：

# 创建数据
data <- c(23, 45, 67, 89, 12, 34, 56, 78, 90, 43)

# 基本统计量
mean_val <- mean(data)
median_val <- median(data)
sd_val <- sd(data)

# 输出结果
cat("均值:", mean_val, "\\n")
cat("中位数:", median_val, "\\n")
cat("标准差:", sd_val, "\\n")

# 创建直方图
hist(data, main="数据分布", xlab="值", col="lightblue")`,
  output: '欢迎使用R语言编程环境！\n点击"运行"按钮执行您的代码。',
  isRunning: false,
  error: null,
  plots: [],
  currentPlotIndex: 0,
  dataFrames: [],
  currentDataFrameIndex: 0,
  newPlotDetected: false,
  newDataFrameDetected: false,
  executionHistory: [],

  setCode: (code: string) => {
    set({ code });
  },

  setOutput: (output: string) => {
    set({ output });
  },

  setRunning: (isRunning: boolean) => {
    set({ isRunning });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  clearOutput: () => {
    set({ output: '', error: null, plots: [], currentPlotIndex: 0, dataFrames: [], currentDataFrameIndex: 0, executionHistory: [] });
  },

  resetDetectionStates: () => {
    set({ newPlotDetected: false, newDataFrameDetected: false });
  },

  markPlotAsSeen: () => {
    set({ newPlotDetected: false });
  },

  markDataFrameAsSeen: () => {
    set({ newDataFrameDetected: false });
  },

  setCurrentDataFrame: (index: number) => {
    set({ currentDataFrameIndex: index });
  },

  setCurrentPlot: (index: number) => {
    set({ currentPlotIndex: index });
  },

  addExecutionStep: (step: import('../types').ExecutionStep) => {
    set((state: EditorState) => ({
      executionHistory: [...state.executionHistory, step]
    }));
  },

  runCode: async () => {
    const { code, setRunning, setOutput, setError, addExecutionStep } = get();

    if (!code.trim()) {
      setError('请输入要执行的R代码');
      return;
    }

    // Clear execution history and start fresh
    set({ executionHistory: [] });
    // Reset captured dataframes tracking
    webrService.resetCapturedDataFrames();
    setRunning(true);
    setError(null);
    setOutput('正在初始化R环境...\n');

    try {
      // 尝试使用真实的WebR执行
      try {
        await webrService.initialize();

        if (webrService.isReady()) {
          setOutput('正在执行代码...\n');
          const result = await webrService.executeCode(code);

          // Add to execution history
          addExecutionStep({
            code: code,
            output: result.output || '',
            error: result.error,
            plots: result.plots || [],
            dataFrame: result.dataFrame || null,
            timestamp: Date.now()
          });

          if (result.error) {
            setError(result.error);
          } else {
            let output = result.output;

            // 添加绘图信息
            if (result.plots && result.plots.length > 0) {
              // If there are plots but no text output, show plot message
              if (!output) {
                output = `生成了 ${result.plots.length} 个图表`;
              } else {
                output += `\n\n生成了 ${result.plots.length} 个图表`;
              }
            }

            // Only update output if there's actual content
            if (output) {
              setOutput(output);
            }

            // Store plots and dataframe in the editor store for display
            const currentPlots = get().plots;
            const hasNewPlots = (result.plots || []).length > 0;
            const hasNewDataFrame = result.dataFrame !== null;

            const newPlots = [...currentPlots, ...(result.plots || [])];
            const newDataFrames = result.dataFrame ? [...get().dataFrames, result.dataFrame] : get().dataFrames;

            set({
              plots: newPlots,
              currentPlotIndex: hasNewPlots ? currentPlots.length : get().currentPlotIndex,
              dataFrames: newDataFrames,
              currentDataFrameIndex: result.dataFrame ? newDataFrames.length - 1 : get().currentDataFrameIndex,
              newPlotDetected: hasNewPlots,
              newDataFrameDetected: hasNewDataFrame
            });
            return;
          }
        }
      } catch (webrError) {
        console.warn('WebR initialization failed, falling back to simulation mode:', webrError);
        setOutput('WebR加载失败，使用模拟模式执行...\n\n');
      }

      // 如果WebR不可用或初始化失败，使用模拟执行
      const result = await simulateRExecution(code);
      addExecutionStep({
        code: code,
        output: result,
        error: null,
        plots: [],
        dataFrame: null,
        timestamp: Date.now()
      });
      setOutput(result);

    } catch (error) {
      console.error('R execution error:', error);
      const errorMsg = error instanceof Error ? error.message : '代码执行失败';
      setError(errorMsg);
      setOutput(`错误: ${errorMsg}`);
      addExecutionStep({
        code: code,
        output: '',
        error: errorMsg,
        plots: [],
        dataFrame: null,
        timestamp: Date.now()
      });
    } finally {
      setRunning(false);
    }
  },

  runSelection: async (selectedCode: string) => {
    if (!selectedCode.trim()) {
      get().setError('请先选择要执行的R代码');
      return;
    }

    const { setRunning, setOutput, setError, addExecutionStep } = get();

    setRunning(true);
    setError(null);

    try {
      // 尝试使用真实的WebR执行选中的代码
      try {
        await webrService.initialize();

        if (webrService.isReady()) {
          setOutput('正在执行选中的代码...\n');
          const result = await webrService.runSelection(selectedCode);

          console.log('runSelection result:', result);

          // Add to execution history
          addExecutionStep({
            code: selectedCode,
            output: result.output || '',
            error: result.error,
            plots: result.plots || [],
            dataFrame: result.dataFrame || null,
            timestamp: Date.now()
          });

          if (result.error) {
            setError(result.error);
          } else {
            let output = result.output;

            // 添加绘图信息
            if (result.plots && result.plots.length > 0) {
              // If there are plots but no text output, show plot message
              if (!output) {
                output = `生成了 ${result.plots.length} 个图表`;
              } else {
                output += `\n\n生成了 ${result.plots.length} 个图表`;
              }
            }

            // Add dataframe message if detected
            if (result.dataFrame) {
              if (!output) {
                output = `数据表: ${result.dataFrame.name} (${result.dataFrame.rowCount} × ${result.dataFrame.colCount})`;
              } else {
                output += `\n\n数据表: ${result.dataFrame.name} (${result.dataFrame.rowCount} × ${result.dataFrame.colCount})`;
              }
            }

            // Only append if there's actual content to show
            if (output) {
              // Append to existing output with separator
              const currentOutput = get().output;
              const separator = '\n\n--- 选中的代码执行结果 ---\n\n';
              setOutput(currentOutput + separator + output);
            }

            // Store plots and dataframe in the editor store for display
            const currentPlots = get().plots;
            const hasNewPlots = (result.plots || []).length > 0;
            const hasNewDataFrame = result.dataFrame !== null;

            const newPlots = [...currentPlots, ...(result.plots || [])];
            const newDataFrames = result.dataFrame ? [...get().dataFrames, result.dataFrame] : get().dataFrames;

            set({
              plots: newPlots,
              currentPlotIndex: hasNewPlots ? currentPlots.length : get().currentPlotIndex,
              dataFrames: newDataFrames,
              currentDataFrameIndex: result.dataFrame ? newDataFrames.length - 1 : get().currentDataFrameIndex,
              newPlotDetected: hasNewPlots,
              newDataFrameDetected: hasNewDataFrame
            });
            return;
          }
        }
      } catch (webrError) {
        console.warn('WebR initialization failed, falling back to simulation mode:', webrError);
        setOutput('WebR加载失败，使用模拟模式执行...\n\n');
      }

      // 如果WebR不可用或初始化失败，使用模拟执行
      const result = await simulateRExecution(selectedCode);
      const currentOutput = get().output;
      setOutput(currentOutput + '\n\n--- 选中的代码执行结果 ---\n\n' + result);

      // Add to execution history
      addExecutionStep({
        code: selectedCode,
        output: result,
        error: null,
        plots: [],
        dataFrame: null,
        timestamp: Date.now()
      });

    } catch (error) {
      console.error('R selection execution error:', error);
      const errorMsg = error instanceof Error ? error.message : '代码执行失败';
      setError(errorMsg);
      setOutput(`错误: ${errorMsg}`);
      addExecutionStep({
        code: selectedCode,
        output: '',
        error: errorMsg,
        plots: [],
        dataFrame: null,
        timestamp: Date.now()
      });
    } finally {
      setRunning(false);
    }
  }
}));

// 模拟R代码执行
async function simulateRExecution(code: string): Promise<string> {
  await new Promise(resolve => setTimeout(resolve, 1500)); // 模拟执行延迟

  let output = '';

  // 模拟基本统计计算
  if (code.includes('mean(data)')) {
    const data = [23, 45, 67, 89, 12, 34, 56, 78, 90, 43];
    const mean = data.reduce((a, b) => a + b) / data.length;
    output += `均值: ${mean.toFixed(2)}\n`;
  }

  if (code.includes('median(data)')) {
    const data = [23, 45, 67, 89, 12, 34, 56, 78, 90, 43];
    data.sort((a, b) => a - b);
    const median = data.length % 2 === 0
      ? (data[data.length/2 - 1] + data[data.length/2]) / 2
      : data[Math.floor(data.length/2)];
    output += `中位数: ${median}\n`;
  }

  if (code.includes('sd(data)')) {
    const data = [23, 45, 67, 89, 12, 34, 56, 78, 90, 43];
    const mean = data.reduce((a, b) => a + b) / data.length;
    const variance = data.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / data.length;
    const sd = Math.sqrt(variance);
    output += `标准差: ${sd.toFixed(2)}\n`;
  }

  if (code.includes('hist')) {
    output += '\n直方图已生成（模拟）\n';
    output += '数据分布显示为近似正态分布\n';
  }

  if (code.includes('plot')) {
    output += '\n散点图已生成（模拟）\n';
    output += '图表显示了变量之间的关系\n';
  }

  if (code.includes('summary')) {
    output += '\n数据摘要:\n';
    output += 'Min. 1st Qu.  Median    Mean 3rd Qu.    Max.\n';
    output += '12.0   34.5   50.0   50.7   67.8   90.0\n';
  }

  if (output === '') {
    output = '代码执行完成（模拟模式）\n';
  }

  return output;
}